import React, { useEffect, useMemo, useRef, useState } from "react";

import manWomanBoxes from "assets/supergasbras/images/manWomanBoxes.svg";
import CoverageAreaMap from "components/CoverageAreaMap";
import CardProductCovered from "components/CardProductCovered";
import { CardPageHeader, TabsNavigation } from "ctech-react-components";
import NameStatusCard from "components/NameStatusCard";

import {
  handleGetWorkingAreaRequest,
  handleGetWorkingAreasRequest,
  handleUpdateWorkingHourRequest,
} from "store/modules/Logistics/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";

import {
  handleGetCatalogAreaRequest,
  handleGetCatalogProductsRequest,
} from "store/modules/Catalog/thunk.store";
import { useAuth } from "context/AuthContext";
import dealerCareApi from "services/dealerCareApi";

import Grid from "@mui/material/Grid";

import AccordionProductCovered from "components/AccordionProductCovered";
import WorkingHour from "components/WorkingHour";
import {
  getCoverageAreaDealerId,
  supportsCoverageAreaDriverRegistration,
} from "store/modules/Logistics/logisticsCoverageArea.helpers";
import {
  CoverageAreaProps,
  CoverageAreaSummary,
  WorkingHourProps,
} from "store/modules/Logistics/types.store";
import { ECoverageAreaType } from "enums/options/coverageArea";
import * as Styled from "./styles";
import CardDriverArea from "components/CardDriverArea";
import CoverageAreaQRCode from "components/CoverageAreaQRCode";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Typography,
  Button as MuiButton,
  Modal,
} from "@mui/material";
import Button from "components/atoms/Button";
import DrawerMenu from "components/DrawerMenu";
import MassEditPriceMenu from "./components/MassEditPriceMenu";
import MassEditWorkingHoursMenu from "./components/MassEditWorkingHoursMenu";
import { toast } from "react-toastify";
import { useProductsAndCoverageDealerDrivers } from "./hooks/useProductsAndCoverageDealerDrivers";

const ProductsAndCoverage: React.FC = () => {
  const [currentTabValue, setCurrentTabValue] = useState("initial");
  const dispatch = useAppDispatch();
  const { dealerId } = useAuth();
  const { t } = useTranslation();
  const { dealerDrivers, ensureDealerDriversLoaded } =
    useProductsAndCoverageDealerDrivers(dealerId);
  const { workingAreas, workingAreaDetails } = useAppSelector(
    (state) => state.logistics,
  );
  const { coverageArea: catalogCoverageAreas } = useAppSelector(
    (state) => state.catalog,
  );
  const [isMassEdit, setIsMassEdit] = useState(false);
  const [isMassEditPrice, setIsMassEditPrice] = useState(false);
  const [isMassEditWorkingHours, setIsMassEditWorkingHours] = useState(false);
  const [massEditData, setMassEditData] = useState<CoverageAreaProps[]>([]);
  const [isCancelEditModalOpen, setIsCancelEditModalOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [tabsHeight, setTabsHeight] = useState(0);
  const [gasDoPovo, setGasDoPovo] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const tabsNavigationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dealerId) return;

    const fetchGasDoPovo = async () => {
      try {
        const response = await dealerCareApi.get(`v1/dealers/${dealerId}`);
        const dealerData = response?.data?.data;
        setGasDoPovo(dealerData?.attributes?.gasDoPovo ?? false);
      } catch {
        setGasDoPovo(false);
      }
    };

    fetchGasDoPovo();
  }, [dealerId]);

  const handleChangeTab = async (
    _: React.SyntheticEvent | null,
    newValue: string,
  ) => {
    if (newValue !== "initial") {
      const areaDetailPromise = dispatch(
        handleGetWorkingAreaRequest({
          coverageAreaId: newValue,
          dealerId,
          suppressGlobalSpinner: true,
        }),
      ).unwrap();

      const catalogPromise = dispatch(
        handleGetCatalogAreaRequest({
          coverageAreaId: newValue,
          dealerId,
        }),
      ).unwrap();

      const areaDetail = await areaDetailPromise;

      const driversPromise = supportsCoverageAreaDriverRegistration(
        areaDetail.type,
      )
        ? ensureDealerDriversLoaded()
        : Promise.resolve([]);

      await Promise.all([catalogPromise, driversPromise]);
      setCurrentTabValue(newValue);
    } else {
      setCurrentTabValue(newValue);
    }
  };

  const Tabs = useMemo(() => {
    const initialTabs = [{ label: t("all"), value: "initial" }];

    workingAreas?.forEach((workingArea) =>
      initialTabs.push({ label: workingArea.name, value: workingArea.id }),
    );

    return initialTabs;
  }, [workingAreas, t]);

  const currentCoverageArea = useMemo(() => {
    return workingAreaDetails[currentTabValue] ?? null;
  }, [workingAreaDetails, currentTabValue]);

  useEffect(() => {
    dispatch(handleGetWorkingAreasRequest({ dealerId }));
    dispatch(handleGetCatalogProductsRequest());
  }, [dispatch, dealerId]);

  useEffect(() => {
    const updateTabsHeight = () => {
      if (tabsNavigationRef.current) {
        setTabsHeight(tabsNavigationRef.current.offsetHeight);
      }
    };

    updateTabsHeight();
    window.addEventListener("resize", updateTabsHeight);
    return () => window.removeEventListener("resize", updateTabsHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      const headerRect = headerRef.current.getBoundingClientRect();
      const headerHeight = headerRect.height;
      const headerTop = headerRect.top;

      // Verifica se o header está 50% fora da tela (para cima)
      const is50PercentOut = headerTop + headerHeight * 0.5 < 0;

      setIsHeaderVisible(!is50PercentOut);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleUpdateWorkingArea = async (
    openingHours: WorkingHourProps[],
    holidayOpeningHours: any[],
  ) => {
    await dispatch(
      handleUpdateWorkingHourRequest({
        coverageAreaId: currentCoverageArea?.id || "",
        openingHours,
        dealerId: getCoverageAreaDealerId(currentCoverageArea),
        holidayOpeningHours: [holidayOpeningHours],
      }),
    ).unwrap();
  };

  const handleMassEdit = () => {
    if (isMassEdit) {
      setIsCancelEditModalOpen(true);
    } else {
      setIsMassEdit(true);
      setIsCancelEditModalOpen(false);
    }
  };

  const handleGetCoverageAreaProductsAndPrices = async (
    coverageAreaId: string,
  ) => {
    const coverageAreaProductsAndPrices = await dispatch(
      handleGetCatalogAreaRequest({ coverageAreaId, dealerId }),
    ).unwrap();
    return coverageAreaProductsAndPrices?.data?.productPrices ?? [];
  };

  const handleMassEditData = async (data: CoverageAreaSummary) => {
    const coverageAreaDetail = await dispatch(
      handleGetWorkingAreaRequest({
        dealerId,
        coverageAreaId: data.id,
        suppressGlobalSpinner: true,
      }),
    ).unwrap();

    if (!coverageAreaDetail.enabled) {
      toast.error(
        "Mapa desabilitado, para selecioná-lo ative-o e tente novamente!",
      );
      return;
    }

    const coverageAreaProductsAndPrices =
      await handleGetCoverageAreaProductsAndPrices(data.id);

    const coverageAreaWithProductsAndPrices = {
      ...coverageAreaDetail,
      productPrices: coverageAreaProductsAndPrices,
    };

    setMassEditData((prev: CoverageAreaProps[]) => {
      if (
        prev.some((item) => item.id === coverageAreaWithProductsAndPrices.id)
      ) {
        return prev.filter(
          (item) => item.id !== coverageAreaWithProductsAndPrices.id,
        );
      }
      return [...prev, coverageAreaWithProductsAndPrices];
    });
  };

  const handleSelectAll = async () => {
    if (massEditData.length > 0) {
      setMassEditData([]);
    } else {
      const loadedWorkingAreas = await Promise.all(
        workingAreas?.map(async (workingArea) => {
          const coverageAreaDetail = await dispatch(
            handleGetWorkingAreaRequest({
              dealerId,
              coverageAreaId: workingArea.id,
              suppressGlobalSpinner: true,
            }),
          ).unwrap();
          if (!coverageAreaDetail.enabled) return null;

          const coverageAreaProductsAndPrices =
            await handleGetCoverageAreaProductsAndPrices(workingArea.id);
          return {
            ...coverageAreaDetail,
            productPrices: coverageAreaProductsAndPrices,
          };
        }) || [],
      );
      const workingAreasWithProductsAndPrices = loadedWorkingAreas.filter(
        Boolean,
      ) as CoverageAreaProps[];
      if (workingAreasWithProductsAndPrices.length !== workingAreas?.length) {
        toast.warning("Existem áreas desativadas que não podem ser editadas!");
      }
      setMassEditData(workingAreasWithProductsAndPrices);
    }
  };

  const handleCloseCancelEditModal = () => {
    setIsMassEdit(false);
    setIsCancelEditModalOpen(false);
    setMassEditData([]);
  };

  const handleEditWorkingHours = () => {
    if (isMassEditWorkingHours) {
      setIsMassEditWorkingHours(false);
      handleCloseCancelEditModal();
    } else {
      setIsMassEditWorkingHours(true);
    }
  };

  const handleEditPrices = () => {
    if (isMassEditPrice) {
      setIsMassEditPrice(false);
      handleCloseCancelEditModal();
    } else {
      setIsMassEditPrice(true);
    }
  };

  return (
    <Styled.Container selectedArea={currentTabValue !== "initial"}>
      <Styled.TabsNavigationWrapper ref={tabsNavigationRef}>
        <TabsNavigation
          onChangeTab={handleChangeTab}
          selectedTab={currentTabValue}
          tabs={Tabs}
        />
      </Styled.TabsNavigationWrapper>

      <Styled.Content>
        {currentTabValue === "initial" || !currentCoverageArea ? (
          <>
            <Styled.HeaderWrapper ref={headerRef} $isVisible={isHeaderVisible}>
              <CardPageHeader
                urlImage={manWomanBoxes}
                title={
                  <Typography variant="h5">
                    {t("productsAndCoverage")}
                  </Typography>
                }
              />
            </Styled.HeaderWrapper>
            <Styled.MassEditContainer
              $isFixed={!isHeaderVisible}
              $tabsHeight={tabsHeight}
            >
              {isMassEdit && massEditData.length > 0 && (
                <Typography variant="body2" color="primary">
                  {t("areasSelected", { count: massEditData.length })}
                </Typography>
              )}
              <Styled.MassEditButtonContainer>
                {isMassEdit && (
                  <>
                    <MuiButton
                      disabled={massEditData.length === 0}
                      variant="text"
                      color={massEditData.length === 0 ? "inherit" : "primary"}
                      onClick={handleEditWorkingHours}
                      sx={{ textTransform: "uppercase" }}
                    >
                      {t("editWorkingHours").toUpperCase()}
                    </MuiButton>
                    <MuiButton
                      disabled={massEditData.length === 0}
                      variant="text"
                      color={massEditData.length === 0 ? "inherit" : "primary"}
                      onClick={handleEditPrices}
                      sx={{ textTransform: "uppercase" }}
                    >
                      {t("editPrices").toUpperCase()}
                    </MuiButton>
                  </>
                )}
                <Button
                  variant={isMassEdit ? "contained" : "outlined"}
                  color={isMassEdit ? "primary" : "inherit"}
                  onClick={handleMassEdit}
                >
                  {isMassEdit
                    ? t("cancelEdit").toUpperCase()
                    : t("editInBatch").toUpperCase()}
                </Button>
              </Styled.MassEditButtonContainer>
            </Styled.MassEditContainer>
            {isMassEdit && (
              <Styled.SelectAllContainer>
                <Checkbox
                  checked={massEditData.length === workingAreas?.length}
                  onChange={handleSelectAll}
                />
                <Typography variant="body2">{t("selectAll")}</Typography>
              </Styled.SelectAllContainer>
            )}
            <Grid
              container
              spacing={{ xs: 2, md: 3 }}
              columns={{ xs: 4, sm: 8, md: 12 }}
            >
              {workingAreas &&
                workingAreas?.map((product) => (
                  <Grid item xs={6} md={6} sm={12} key={product.id}>
                    <AccordionProductCovered
                      handleOpenArea={(value: string) => {
                        return handleChangeTab(null, value);
                      }}
                      product={product}
                      workingAreasId={product.id}
                      isMassEdit={isMassEdit}
                      handleMassEditData={handleMassEditData}
                      massEditData={massEditData}
                      gasDoPovo={gasDoPovo}
                    />
                  </Grid>
                ))}
            </Grid>
          </>
        ) : (
          <Styled.Row>
            <Styled.Column>
              <NameStatusCard
                workingAreaData={currentCoverageArea}
                goBack={() => handleChangeTab(null, "initial")}
              />

              {supportsCoverageAreaDriverRegistration(
                currentCoverageArea?.type,
              ) && (
                <CardDriverArea
                  currentCoverageArea={currentCoverageArea}
                  dealerDrivers={dealerDrivers}
                  dealerSelected={currentTabValue}
                />
              )}

              <CardProductCovered
                coverageArea={
                  catalogCoverageAreas?.find(
                    (area) => area.coverageAreaId === currentTabValue,
                  ) as any
                }
                dealerSelected={currentTabValue}
                gasDoPovo={gasDoPovo}
                workingAreaAreaType={currentCoverageArea?.type}
              />
            </Styled.Column>

            <Styled.Column>
              {currentCoverageArea.type === ECoverageAreaType.DealerStore ||
              currentCoverageArea.type === ECoverageAreaType.DriverStore ? (
                <CoverageAreaQRCode
                  coverageAreaURL={currentCoverageArea.storeUri}
                  coverageAreaName={currentCoverageArea.name}
                  currentAreaCoverage={currentCoverageArea}
                />
              ) : (
                <CoverageAreaMap
                  currentCoverageArea={currentCoverageArea}
                  coverageAreaId={currentCoverageArea?.id}
                  selectedAreaData={currentCoverageArea?.map?.geoJson}
                />
              )}

              <WorkingHour
                workingHourData={currentCoverageArea?.openingHours}
                onUpdate={handleUpdateWorkingArea}
                dealerSelected={currentTabValue}
              />
            </Styled.Column>
          </Styled.Row>
        )}
      </Styled.Content>
      <Modal
        open={isCancelEditModalOpen}
        onClose={() => setIsCancelEditModalOpen(false)}
      >
        <Styled.CancelEditModalContainer>
          <Typography variant="h6">{t("cancelEditModalTitle")}</Typography>
          <Typography variant="body2">{t("cancelEditModalContent")}</Typography>
          <Styled.CancelEditModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsCancelEditModalOpen(false)}
            >
              {t("cancelEditModalCancel").toUpperCase()}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseCancelEditModal}
            >
              {t("cancelEditModalClose").toUpperCase()}
            </Button>
          </Styled.CancelEditModalButtonsContainer>
        </Styled.CancelEditModalContainer>
      </Modal>
      <DrawerMenu open={isMassEditPrice} onClose={handleEditPrices}>
        <MassEditPriceMenu
          massEditData={massEditData}
          handleCloseMassEditPrice={handleEditPrices}
        />
      </DrawerMenu>
      <DrawerMenu
        open={isMassEditWorkingHours}
        onClose={handleEditWorkingHours}
        width={605}
      >
        <MassEditWorkingHoursMenu
          handleCloseMassEditWorkingHours={handleEditWorkingHours}
          massEditData={massEditData}
        />
      </DrawerMenu>
    </Styled.Container>
  );
};

export default ProductsAndCoverage;
